// apps/web/src/pages/author/PreviewPane.tsx
//
// Phase 8 Plan 10 Task 1 — Live preview pane (authr-04 / D-06).
//
// AUTHR-06-EXCEPTION (W14): PreviewPane is the SINGLE EXCEPTION to the
// per-path useWatch discipline (08-RESEARCH §"Pattern 3"). Every other
// component in apps/web/src/pages/author/ must use `useWatch({ control,
// name: '<specific.path>' })` so the per-keystroke render fan-out stays
// small. PreviewPane DELIBERATELY subscribes to the entire form
// (`useWatch({ control })`) — the preview WANTS to re-render on every
// committed validated change. The 200ms debounce (use-debounced-recipe)
// + the Zod safeParse gate amortize the cost so the AUTHR-06 keystroke
// budget is NOT charged the preview's render time on the typing path.
// AUTHR-06 bench (Plan 12) measures the actual keystroke render time of
// the FORM panel only, not this debounced pipeline.
//
// VISUAL CONTRACT: UI-SPEC §"Live Preview Pane Visual Contract"
// (lines 566-670). Structure (in order):
//   <aside id="preview-pane" aria-label="Live recipe preview" ...>
//     <header>     "Preview" eyebrow label
//     [StaleStateBanner] when safeParse fails — role="status" aria-live="polite"
//                        copy (WR-07 update): "Preview paused — showing the
//                        last valid recipe. Fix form errors to resume live
//                        updates."
//     <div>        empty-state OR widget loop wrapped in RecipeStateProvider
//
// RENDERING PRIMITIVES (CONTEXT D-12 — preview === rendered recipe):
//   - RecipeRenderEngine (apps/web/src/render/RecipeRenderEngine.tsx) is the
//     SAME form-context-free render engine the view routes and the SpreadComposer
//     mount. As of Phase 11.2 Plan 05 (C1) the preview's success body is the
//     engine, so the preview renders the constraint overlay + the REND-05
//     disabled-option tooltip — one tooltip contract across all three engine
//     call sites. This is an INTENDED parity ADD (the prior bare widget loop
//     had no constraint overlay), NOT a regression. The engine owns the widget
//     loop now (including the W2 option-set single-render skip).
//   - RecipeStateProvider + memoryStrategy give the preview an isolated
//     per-mount state Map — pickers in the preview don't leak into other
//     preview sessions and don't touch any persisted recipe state. The engine
//     renders INSIDE this caller-supplied provider (CONTEXT A2 — the engine
//     does not self-wrap one), so the preview keeps owning its own provider wrap.
//   - previewRecipeId is stable for the editor's lifetime (useMemo +
//     crypto.randomUUID) so React.memo identity inside the engine's subtree
//     holds across debounced re-renders.
//   - TOOLTIP/VOCAB ANCESTORS: the engine's RecipeOptionGroup renders Radix
//     <Tooltip> (REND-05) and calls useTerm(), so it requires a <TooltipProvider>
//     AND a <SpreadVocabularyProvider> ancestor. RecipeEditor.tsx already wraps
//     the preview mount in BOTH (SpreadVocabularyProvider at ~L697, TooltipProvider
//     at ~L698 — confirmed: rightChildren={<PreviewPane/>} renders inside that
//     subtree), so PreviewPane adds NO new provider of its own. A test that mounts
//     PreviewPane standalone supplies its own TooltipProvider + SpreadVocabularyProvider.
//
// LAST-VALID FALLBACK: when safeParse fails (user typed something that
// breaks the schema), the preview keeps showing the LAST VALID recipe
// state in lastValidRef. The StaleStateBanner surfaces the divergence.
// When validation recovers, the preview catches up to the new validated
// form state and the banner disappears.

import {
  type RecipeDefinitionV1,
  RecipeDefinitionV1 as RecipeDefinitionV1Schema,
} from "@the-recipe-spread/shared";
import { Eye } from "lucide-react";
import { useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { RecipeRenderEngine } from "@/render/RecipeRenderEngine";
import { memoryStrategy, RecipeStateProvider } from "@/widgets/recipe-state-provider";
import { useDebouncedRecipe } from "./use-debounced-recipe";

// WR-06 — DEBOUNCE_MS is a module-level constant. The underlying
// useDebouncedRecipe hook reschedules its timer on EITHER value OR delay
// changes; because PreviewPane never changes this value at runtime, the
// trailing-edge contract holds. If a future plan ever lifts DEBOUNCE_MS
// into a prop, the hook will drop the trailing-edge fire when the prop
// changes mid-window — re-evaluate the hook's contract at that point.
const DEBOUNCE_MS = 200;
// WR-07 — explicit "last successful state" copy so authors know the
// preview below the banner is the snapshot from BEFORE the current
// invalid form mutation. Without this, a deleted widget that's still
// in lastValidRef could be mistaken for the live tree.
const STALE_BANNER_COPY =
  "Preview paused — showing the last valid recipe. Fix form errors to resume live updates.";
const EMPTY_STATE_TITLE = "Add a widget to see a live preview.";

export function PreviewPane(): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();

  // AUTHR-06-EXCEPTION (see file header): deliberate top-level useWatch
  // subscription. Every form mutation flows here; the 200ms debounce +
  // safeParse gate cover the perf budget.
  const liveRecipe = useWatch({ control });
  const debouncedRecipe = useDebouncedRecipe(liveRecipe, DEBOUNCE_MS);

  // Defensive safeParse — the form may transiently hold an invalid shape
  // (e.g., a half-typed title that violates min(1)) between blurs.
  const validation = useMemo(
    () => RecipeDefinitionV1Schema.safeParse(debouncedRecipe),
    [debouncedRecipe],
  );

  // Last-valid fallback: when safeParse fails, keep showing the last
  // validated snapshot in the live tree below; the StaleStateBanner
  // explains the divergence to the user.
  const lastValidRef = useRef<RecipeDefinitionV1 | null>(null);
  if (validation.success) {
    lastValidRef.current = validation.data;
  }
  const validRecipe: RecipeDefinitionV1 | null = validation.success
    ? validation.data
    : lastValidRef.current;

  // Stable previewRecipeId across the editor's lifetime. crypto.randomUUID
  // is browser-native in jsdom + chromium. The "preview:" namespace makes
  // log lines / strategy hooks distinguishable from production recipe ids.
  const previewRecipeId = useMemo(() => `preview:${crypto.randomUUID()}`, []);

  const widgets = validRecipe?.widgets ?? [];

  // MD-01 — synthesize the engine's single batch set with a STABLE identity.
  // useSpreadConstraintEvaluation (inside RecipeRenderEngine) memoizes its
  // EvalContext keyed on the batchSets reference; a fresh array literal per
  // render would defeat that memo and drive a perpetual constraint
  // re-evaluation / re-render loop on top of the debounced preview pipeline.
  // Keyed on the recipe id (or the "preview" sentinel), this yields one stable
  // reference across the debounced re-renders. The preview shows one recipe, so
  // the array is single-batch and the engine renders it at activeBatchSetIndex=0.
  const previewRecipeKey = (validRecipe as { id?: string } | null)?.id ?? "preview";
  const batchSets = useMemo(() => [{ recipeId: previewRecipeKey }], [previewRecipeKey]);

  return (
    <aside
      id="preview-pane"
      aria-label="Live recipe preview"
      className="flex h-full flex-col bg-[var(--recipe-bg)]"
    >
      <header className="flex items-center justify-between border-b border-border bg-background px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Preview
        </span>
      </header>
      {!validation.success ? (
        <div
          role="status"
          aria-live="polite"
          className="border-b border-destructive/30 bg-destructive/10 px-3 py-1 text-xs text-destructive"
        >
          {STALE_BANNER_COPY}
        </div>
      ) : null}
      <div className="flex-1 overflow-auto p-4">
        {validRecipe === null || widgets.length === 0 ? (
          <EmptyState icon={Eye} title={EMPTY_STATE_TITLE} />
        ) : (
          // C1 (Phase 11.2 Plan 05): the success body is the shared
          // RecipeRenderEngine — the SAME engine the view routes + SpreadComposer
          // mount. It owns the widget loop (and the W2 option-set single-render
          // skip), adds render-time constraint evaluation, and surfaces the
          // REND-05 disabled-option tooltip the prior bare loop lacked (intended
          // parity ADD, not a regression). The engine renders INSIDE this
          // caller-supplied provider (CONTEXT A2), so PreviewPane keeps its own
          // RecipeStateProvider wrap (memoryStrategy = isolated, ephemeral state).
          // batchSets is synthesized single-batch (the preview shows one recipe);
          // activeBatchSetIndex=0 selects it. No new TooltipProvider/
          // SpreadVocabularyProvider here — RecipeEditor's ancestors (~L697/L698)
          // enclose this mount (see file header).
          <RecipeStateProvider recipeId={previewRecipeId} strategy={memoryStrategy}>
            <RecipeRenderEngine
              recipe={validRecipe}
              batchSets={batchSets}
              activeBatchSetIndex={0}
            />
          </RecipeStateProvider>
        )}
      </div>
    </aside>
  );
}
